import { seasonOfDay } from './plannedWeek';
import type { StockingSeason } from './types';

/**
 * The cycle naming convention, `26A`: a two-digit year and S (spring, January–June) or
 * A (autumn, July–December). It mirrors the backend suggester
 * (`backend/.../fishGroups/domain/stocking-cycle-name.ts`), which prefills a cycle created
 * without a name, so a name typed here and one left to the server read the same.
 */
const SEASON_CODE: Record<StockingSeason, string> = {
    spring: 'S',
    autumn: 'A',
};

export function stockingCycleBaseName(season: StockingSeason, year: number): string {
    return `${String(year % 100).padStart(2, '0')}${SEASON_CODE[season]}`;
}

/**
 * The `26A` of a cycle's planned stocking (`YYYY-MM-DD`), whatever the cycle is called: the
 * name is free text, the planned date is what the backend derives the season from. Null
 * without a valid date.
 */
export function plannedSeasonCode(plannedStartDate: string | null): string | null {
    if (!plannedStartDate) return null;
    const day = new Date(`${plannedStartDate}T00:00:00Z`);
    if (Number.isNaN(day.getTime())) return null;
    return stockingCycleBaseName(seasonOfDay(day), day.getUTCFullYear());
}

/**
 * `base`, or `base-2`, `base-3`, ... when a cycle at the site already has it. Names are unique
 * per site ignoring case, the same rule the backend applies.
 */
export function suggestStockingCycleName(
    season: StockingSeason,
    year: number,
    existingNames: readonly string[]
): string {
    const base = stockingCycleBaseName(season, year);
    const taken = new Set(existingNames.map((name) => name.trim().toLowerCase()));
    if (!taken.has(base.toLowerCase())) return base;
    for (let suffix = 2; ; suffix += 1) {
        const candidate = `${base}-${suffix}`;
        if (!taken.has(candidate.toLowerCase())) return candidate;
    }
}

export function isStockingCycleNameTaken(name: string, existingNames: readonly string[]): boolean {
    const wanted = name.trim().toLowerCase();
    return existingNames.some((existing) => existing.trim().toLowerCase() === wanted);
}
